import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  Chip,
  Theme,
  Link,
  Skeleton,
  IconButton,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { FC, useState, useMemo } from "react";
import { IContent } from "../../../models/admin/cms/contentTypes";
import { ContentListStyles } from "./ContentListStyles";
import CustomDialog from "../../../common/CustomDialog/CustomDialog";
import { slateToHtml } from "../../../common/CMS/SlateToHtml/SlateToHtml";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { RootState } from "../../../store/store";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { deleteContent } from "../CMSActions";
import { formatContentWithStructure } from "../../../common/CMS/FormatContentWithStructure/FormatContentWithStructure";

interface IContentList {
  content: IContent[];
  handleEditClick: (id: string) => void;
}

const ContentList: FC<IContentList> = ({ content, handleEditClick }) => {
  const dispatch = useAppDispatch();
  const [previewModalOpen, setPreviewModalOpen] = useState<boolean>(false);
  const [previewTitle, setPreviewTitle] = useState<string>("");
  const [previewContent, setPreviewContent] = useState<string>("");
  const { isLoading } = useAppSelector((state: RootState) => state.common);

  const processedContent = useMemo(() => {
    return content
      ?.filter(
        (item) => !item?.contentTypeName?.toLowerCase().includes("alert")
      )
      ?.map((item) => ({
        ...item,
        fields: {
          ...item.fields,
          Content:
            typeof item.fields?.Content === "string"
              ? item.fields?.Content
              : slateToHtml(item.fields?.Content),
        },
      }));
  }, [content]);

  const handlePreview = async (id: string) => {
    const selectedCard = processedContent?.filter((item) => item?.id === id);
    setPreviewModalOpen(true);
    setPreviewTitle(selectedCard[0]?.fields?.Heading);
    const content = selectedCard[0]?.fields?.Content;
    const formattedContent = formatContentWithStructure(content);
    setPreviewContent(formattedContent);
  };

  const handlePreviewDialogClose = () => {
    setPreviewModalOpen(false);
    setPreviewTitle("");
  };
  if (!processedContent || !processedContent?.length) return null;
  return (
    <Box
      id="content_list_container"
      data-test-id="content_list_container"
      aria-label="Content List"
    >
      <Box 
        sx={ContentListStyles.headerBox}
        id="content_list_header"
        data-test-id="content_list_header"
        aria-label="Content List Header"
      >
        <Typography 
          variant="h6" 
          sx={ContentListStyles.gradientText}
          id="content_list_title"
          data-test-id="content_list_title"
          aria-label="Content"
        >
          Content
        </Typography>
      </Box>

      <Box 
        sx={ContentListStyles.contentContainer}
        id="content_list_content"
        data-test-id="content_list_content"
        aria-label="Content List Items"
      >
        {isLoading
          ? Array.from(Array(4)).map((_, index) => (
              <Box 
                sx={{ display: "flex", gap: 2 }} 
                key={index}
                id={`content_list_skeleton_${index}`}
                data-test-id={`content_list_skeleton_${index}`}
                aria-label="Loading Content"
              >
                <Skeleton
                  key={index}
                  variant="rounded"
                  width="300px"
                  height={300}
                  animation="wave"
                />
              </Box>
            ))
          : processedContent?.map((item) => (
              <Box 
                key={item.id} 
                sx={ContentListStyles.cardWrapper}
                id={`content_card_${item.id}`}
                data-test-id={`content_card_${item.id}`}
                aria-label={`Content: ${item.fields.Heading}`}
              >
                <Card sx={ContentListStyles.card}>
                  <Box 
                    sx={ContentListStyles.imageContainer}
                    id={`content_image_${item.id}`}
                    data-test-id={`content_image_${item.id}`}
                    aria-label={`Content Image: ${item.fields.Heading}`}
                  >
                    {item.fields?.imageUrl ? (
                      <img
                        src={item.fields?.imageUrl}
                        alt={item.fields.Heading}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <Box 
                        sx={ContentListStyles.noImageBox}
                        id={`content_no_image_${item.id}`}
                        data-test-id={`content_no_image_${item.id}`}
                        aria-label="No Image Available"
                      >
                        <Typography color="textSecondary" id="content_no_image_text" data-test-id="content_no_image_text" aria-label="No Image Available">No Image</Typography>
                      </Box>
                    )}
                    <Box 
                      sx={ContentListStyles.statusChip}
                      id={`content_status_${item.id}`}
                      data-test-id={`content_status_${item.id}`}
                      aria-label={`Content Status: ${item.status}`}
                    >
                      <Chip
                        label={item.status}
                        color={
                          item.status === "Published" ? "success" : "warning"
                        }
                        size="small"
                      />
                    </Box>
                  </Box>

                  <Box 
                    sx={ContentListStyles.tagsContainer}
                    id={`content_tags_${item.id}`}
                    data-test-id={`content_tags_${item.id}`}
                    aria-label="Content Tags"
                  >
                    {item.tags?.map((tag, index) => (
                      <Chip
                        key={index}
                        label={tag}
                        size="small"
                        sx={ContentListStyles.tag}
                        id={`content_tag_${item.id}_${index}`}
                        data-test-id={`content_tag_${item.id}_${index}`}
                        aria-label={`Tag: ${tag}`}
                      />
                    ))}
                  </Box>

                  <CardContent 
                    sx={ContentListStyles.cardContent}
                    id={`content_details_${item.id}`}
                    data-test-id={`content_details_${item.id}`}
                    aria-label={`Content Details: ${item.fields.Heading}`}
                  >
                    <Typography
                      variant="body1"
                      component="h3"
                      sx={{
                        maxHeight: "50px",
                        overflow: "hidden",
                        fontSize: "0.9rem",
                        fontWeight: "600",
                      }}
                      id={`content_heading_${item.id}`}
                      data-test-id={`content_heading_${item.id}`}
                      aria-label={`Content Heading: ${item.fields.Heading}`}
                    >
                      {item?.fields?.Heading}
                    </Typography>
                    <Box
                      sx={ContentListStyles.contentText}
                      dangerouslySetInnerHTML={{
                        __html: item.fields?.Content,
                      }}
                      id={`content_body_${item.id}`}
                      data-test-id={`content_body_${item.id}`}
                      aria-label="Content Body"
                    />
                    {item?.effectiveStartDate && item?.effectiveEndDate && (
                      <Typography
                        sx={{ ...ContentListStyles.contentText, pt: 0.5 }}
                        variant="body2"
                        fontStyle="italic"
                        id={`content_dates_${item.id}`}
                        data-test-id={`content_dates_${item.id}`}
                        aria-label={`Effective from ${item?.effectiveStartDate} to ${item?.effectiveEndDate}`}
                      >
                        Effective from {item?.effectiveStartDate} to{" "}
                        {item?.effectiveEndDate}
                      </Typography>
                    )}
                  </CardContent>

                  <CardActions 
                    sx={ContentListStyles.cardActions}
                    id={`content_actions_${item.id}`}
                    data-test-id={`content_actions_${item.id}`}
                    aria-label="Content Actions"
                  >
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handlePreview(item.id)}
                      id={`content_preview_${item.id}`}
                      data-test-id={`content_preview_${item.id}`}
                      aria-label={`Preview ${item.fields.Heading}`}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleEditClick(item?.id)}
                      id={`content_edit_${item.id}`}
                      data-test-id={`content_edit_${item.id}`}
                      aria-label={`Edit ${item.fields.Heading}`}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => dispatch(deleteContent(item?.id))}
                      id={`content_delete_${item.id}`}
                      data-test-id={`content_delete_${item.id}`}
                      aria-label={`Delete ${item.fields.Heading}`}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </CardActions>
                </Card>
              </Box>
            ))}
      </Box>

      {content?.filter((item) =>
        item?.contentTypeName?.toLowerCase().includes("alert")
      )?.length > 0 && (
        <Box 
          sx={ContentListStyles.headerBox}
          id="content_alerts_header"
          data-test-id="content_alerts_header"
          aria-label="Alerts Header"
        >
          <Typography 
            variant="h6" 
            sx={ContentListStyles.gradientText}
            id="content_alerts_title"
            data-test-id="content_alerts_title"
            aria-label="Alerts"
          >
            Alerts
          </Typography>
        </Box>
      )}

      <Box 
        sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}
        id="content_alerts_list"
        data-test-id="content_alerts_list"
        aria-label="Alerts List"
      >
        {content
          ?.filter((item) =>
            item?.contentTypeName?.toLowerCase().includes("alert")
          )
          ?.map((item) => (
            <Box
              key={item?.id}
              sx={{
                borderRadius: "4px",
                border: "1px solid grey",
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
              id={`alert_item_${item.id}`}
              data-test-id={`alert_item_${item.id}`}
              aria-label={`Alert: ${item?.fields["Banner Text"]}`}
            >
              <Box
                sx={{
                  width: "100%",
                  backgroundColor: (theme: Theme) =>
                    `${
                      item?.fields["Banner Color"]
                        ? item?.fields["Banner Color"]
                        : theme.palette.error.light
                    }`,
                  minHeight: "60px",
                  borderRadius: "4px",
                }}
                id={`alert_banner_${item.id}`}
                data-test-id={`alert_banner_${item.id}`}
                aria-label="Alert Banner"
              >
                <Box 
                  style={ContentListStyles.alertContent}
                  id={`alert_content_${item.id}`}
                  data-test-id={`alert_content_${item.id}`}
                  aria-label="Alert Content"
                >
                  <Typography 
                    sx={ContentListStyles.alertText}
                    id={`alert_text_${item.id}`}
                    data-test-id={`alert_text_${item.id}`}
                    aria-label={`Alert Text: ${item?.fields["Banner Text"]}`}
                  >
                    {item?.fields["Banner Text"]}
                  </Typography>
                  <Link
                    href={item?.fields["Redirect URL"]}
                    sx={ContentListStyles.alertLink}
                    id={`alert_link_${item.id}`}
                    data-test-id={`alert_link_${item.id}`}
                    aria-label={`Alert Link: ${item?.fields["Link Label"]}`}
                  >
                    {item?.fields["Link Label"]}
                  </Link>
                </Box>
              </Box>
              <Box 
                sx={ContentListStyles.tagsContainer}
                id={`alert_tags_${item.id}`}
                data-test-id={`alert_tags_${item.id}`}
                aria-label="Alert Tags"
              >
                {item.tags?.map((tag, index) => (
                  <Chip
                    key={index}
                    label={tag}
                    size="small"
                    sx={ContentListStyles.tag}
                    id={`alert_tag_${item.id}_${index}`}
                    data-test-id={`alert_tag_${item.id}_${index}`}
                    aria-label={`Tag: ${tag}`}
                  />
                ))}
              </Box>
              <Box 
                sx={{ display: "flex", justifyContent: "space-between" }}
                id={`alert_actions_${item.id}`}
                data-test-id={`alert_actions_${item.id}`}
                aria-label="Alert Actions"
              >
                <Box>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleEditClick(item?.id)}
                    id={`alert_edit_${item.id}`}
                    data-test-id={`alert_edit_${item.id}`}
                    aria-label={`Edit Alert: ${item?.fields["Banner Text"]}`}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => dispatch(deleteContent(item?.id))}
                    id={`alert_delete_${item.id}`}
                    data-test-id={`alert_delete_${item.id}`}
                    aria-label={`Delete Alert: ${item?.fields["Banner Text"]}`}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
                <Box sx={{ paddingX: "1rem" }}>
                  <Chip
                    label={item.status}
                    color={item.status === "Published" ? "success" : "warning"}
                    size="small"
                    id={`alert_status_${item.id}`}
                    data-test-id={`alert_status_${item.id}`}
                    aria-label={`Alert Status: ${item.status}`}
                  />
                </Box>
              </Box>
            </Box>
          ))}
      </Box>
      <CustomDialog
        open={previewModalOpen}
        onClose={handlePreviewDialogClose}
        maxWidth="md"
        title={previewTitle}
        content={previewContent}
        showDivider={true}
        isRichTextContent={true}
      />
    </Box>
  );
};

export default ContentList;
